import React from 'react';
import PropTypes from 'prop-types';
import { VaRadio } from '@department-of-veterans-affairs/component-library/dist/react-bindings';
import { formatAddress } from 'platform/forms/address/helpers';
import { USER_ENTERED_ADDRESS_ID } from '../../util/address-validation/getAddressValidationSubmitStrategy';
import {
  getAddressOptionConfig,
  getDynamicHeaderTag,
  getRadioLabelHeaderLevel,
} from '../../util/address-validation/getAddressValidationDisplay';
import { formatDisplayAddressInRadio } from '../../util/contact-information/addressUtils';

/**
 * One block of the validation form: either the address the user entered, or the
 * set of addresses the validation API suggested.
 *
 * Whether the block is selectable is not a property of the block itself — with
 * no suggestions to choose between there is nothing to select, so the
 * user-entered address renders as static text instead. getAddressOptionConfig
 * makes that call.
 *
 * The suggested block is rendered by calling this with an empty address and
 * `id="suggested"`; the addresses themselves come from confirmedSuggestions.
 *
 * @param {object} props
 * @param {object} props.address the user-entered address, or `{}` for the
 *   suggested block.
 * @param {string} [props.id] `'userEntered'` or `'suggested'`.
 */
export const AddressValidationOption = ({
  address,
  confirmedSuggestions,
  id = USER_ENTERED_ADDRESS_ID,
  onChangeSelectedAddress,
  overrideValidationKey,
  selectedAddressId,
}) => {
  const { isAddressFromUser, showRadio, label } = getAddressOptionConfig({
    id,
    confirmedSuggestions,
    overrideValidationKey,
  });

  const { street, cityStateZip, country } = formatAddress(address);

  const Header = getDynamicHeaderTag(5);

  // PORT NOTE: the class passed `description={(street, cityStateZip, country)}`
  // to both radio options. That is a comma operator, not a template — it
  // evaluates to `country` alone, which formatAddress leaves empty for anything
  // but an international address. Reproduced verbatim so the rendered DOM
  // matches the class; fixing it would change what the option reads out.
  const description = country;

  if (!showRadio) {
    return (
      <div className="address-validation-container">
        <Header className="vads-u-margin-top--3 vads-u-padding-top--0">
          Address you entered:
        </Header>
        <div className="vads-u-display--flex vads-u-flex-direction--column vads-u-padding-bottom--1p5">
          <span
            className="dd-privacy-hidden"
            data-dd-action-name="street address"
          >
            {street}
          </span>
          <span
            className="dd-privacy-hidden"
            data-dd-action-name="city, state and zip code"
          >
            {cityStateZip}
          </span>
          <span>{country}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="address-validation-container">
      <VaRadio
        data-testid="va-radio-label"
        label={label}
        labelHeaderLevel={getRadioLabelHeaderLevel()}
        onVaValueChange={event => {
          onChangeSelectedAddress(address, event.detail.value);
        }}
        className={
          isAddressFromUser ? 'vads-u-margin-top--12' : 'vads-u-margin-top--2'
        }
      >
        {isAddressFromUser ? (
          <va-radio-option
            data-testid="userEnteredAddressOption"
            style={{ whiteSpace: 'pre-line' }}
            key="userAddress"
            name="addressGroup"
            label={formatDisplayAddressInRadio(address)}
            description={description}
            value={USER_ENTERED_ADDRESS_ID}
            checked={selectedAddressId === id}
          />
        ) : (
          confirmedSuggestions.map((suggestedAddress, index) => (
            // PORT NOTE: every suggestion shares the constant key the class
            // used, so React logs a duplicate-key warning here exactly as it
            // does for the class. Kept rather than fixed — a stable key is a
            // reconciliation change, and this port is holding output identical.
            <va-radio-option
              data-testid="suggestedAddressOption"
              style={{ whiteSpace: 'pre-line' }}
              key="suggestedAddress"
              name="addressGroup"
              label={formatDisplayAddressInRadio(suggestedAddress)}
              description={description}
              value={index}
              checked={selectedAddressId === index.toString()}
            />
          ))
        )}
      </VaRadio>
    </div>
  );
};

AddressValidationOption.propTypes = {
  address: PropTypes.object,
  confirmedSuggestions: PropTypes.array,
  id: PropTypes.string,
  overrideValidationKey: PropTypes.number,
  selectedAddressId: PropTypes.string,
  onChangeSelectedAddress: PropTypes.func,
};

export default AddressValidationOption;
